import React, { useRef, useEffect } from 'react'
import {
	Box,
	Button,
	Typography,
	Drawer,
	useMediaQuery,
	useTheme
} from '@mui/material'

// Total number of steps in the booking flow (client info, pets, service, date/time)
const TOTAL_STEPS = 4

// Progress Indicator Component
const ProgressIndicator = ({ currentStep, totalSteps = TOTAL_STEPS }) => {
	return (
		<Box sx={{
			display: 'flex',
			justifyContent: 'center',
			alignItems: 'center',
			gap: '8px',
			width: '100%'
		}}>
			{Array.from({ length: totalSteps }, (_, index) => {
				const stepNum = index + 1
				const isCompleted = stepNum < currentStep
				const isCurrent = stepNum === currentStep

				return (
					<Box
						key={stepNum}
						sx={{
							width: isCurrent ? '32px' : '8px',
							height: '8px',
							borderRadius: '4px',
							bgcolor: isCompleted || isCurrent ? '#9F7AEA' : '#E9D8FD',
							transition: 'all 0.3s ease'
						}}
					/>
				)
			})}
		</Box>
	)
}

// Navigation Buttons Component
const NavigationButtons = ({
	onBack,
	onNext,
	backLabel = 'Back',
	nextLabel = 'Next',
	showBack = true,
	showNext = true,
	nextDisabled = false,
	loading = false,
	isMobile = false
}) => {
	if (isMobile) {
		// Mobile: Fixed bottom bar with pill buttons
		return (
			<Box sx={{
				position: 'fixed',
				bottom: 0,
				left: 0,
				right: 0,
				display: 'flex',
				gap: '16px',
				padding: '16px',
				bgcolor: "transparent",
				zIndex: 1300
			}}>
				{showBack && (
					<Button
						onClick={onBack}
						sx={{
							flex: 1,
							py: '12px',
							px: '16px',
							bgcolor: '#FFFFFF',
							border: '1px solid #CBD5E0',
							borderRadius: '9999px',
							color: '#9F7AEA',
							fontFamily: 'Inter',
							fontWeight: 600,
							fontSize: '14px',
							textTransform: 'none',
							'&:hover': {
								bgcolor: '#F7FAFC'
							}
						}}
					>
						{backLabel}
					</Button>
				)}
				{showNext && (
					<Button
						onClick={onNext}
						disabled={nextDisabled || loading}
						sx={{
							flex: showBack ? 2 : 'auto',
							minWidth: showBack ? 'auto' : '200px',
							py: '12px',
							px: '16px',
							bgcolor: '#9F7AEA',
							border: '1px solid #9F7AEA',
							borderRadius: '9999px',
							color: '#FFFFFF',
							fontFamily: 'Inter',
							fontWeight: 600,
							fontSize: '14px',
							textTransform: 'none',
							'&:hover': {
								bgcolor: '#805AD5'
							},
							'&:disabled': {
								bgcolor: '#CBD5E0',
								borderColor: '#CBD5E0',
								color: '#A0AEC0'
							}
						}}
					>
						{loading ? 'Loading...' : nextLabel}
					</Button>
				)}
			</Box>
		)
	}

	// Desktop: Inline buttons
	return (
		<Box sx={{
			display: 'flex',
			justifyContent: 'space-between',
			alignItems: 'center',
			gap: '18px',
			width: '100%'
		}}>
			{showBack ? (
				<Button
					onClick={onBack}
					sx={{
						py: '12px',
						px: '16px',
						minWidth: '100px',
						bgcolor: '#FFFFFF',
						border: '1px solid #9F7AEA',
						borderRadius: '6px',
						color: '#9F7AEA',
						fontFamily: 'Inter',
						fontWeight: 600,
						fontSize: '14px',
						letterSpacing: '-1.79%',
						textTransform: 'none',
						'&:hover': {
							bgcolor: '#F7FAFC'
						}
					}}
				>
					{backLabel}
				</Button>
			) : (
				<Box />
			)}
			{showNext && (
				<Button
					onClick={onNext}
					disabled={nextDisabled || loading}
					sx={{
						py: '12px',
						px: '16px',
						bgcolor: '#E9D8FD',
						border: '1px solid #9F7AEA',
						borderRadius: '6px',
						color: '#2D3748',
						fontFamily: 'Inter',
						fontWeight: 600,
						fontSize: '14px',
						letterSpacing: '-1.79%',
						textTransform: 'none',
						'&:hover': {
							bgcolor: '#D6BCFA'
						},
						'&:disabled': {
							bgcolor: '#E2E8F0',
							borderColor: '#CBD5E0',
							color: '#A0AEC0'
						}
					}}
				>
					{loading ? 'Loading...' : nextLabel}
				</Button>
			)}
		</Box>
	)
}

// Main Wrapper Component
function BookingStepWrapper({
	children,
	currentStep,
	title = 'Book appointment',
	subtitle,
	onBack,
	onNext,
	backLabel,
	nextLabel,
	showBack = true,
	showNext = true,
	nextDisabled = false,
	loading = false,
	maxWidth = 582,
	showProgress = true,
	hideNavigation = false,
	scrollKey // Optional key that triggers scroll-to-top when changed (for sub-steps)
}) {
	const theme = useTheme()
	const isMobile = useMediaQuery(theme.breakpoints.down('md'))
	const contentRef = useRef(null)

	// Scroll to top when step or sub-step changes (mobile only)
	useEffect(() => {
		if (isMobile && contentRef.current) {
			contentRef.current.scrollTo({ top: 0, behavior: 'instant' })
		}
	}, [currentStep, scrollKey, isMobile])

	if (isMobile) {
		// Mobile: Bottom drawer layout
		return (
			<Box sx={{
				display: 'flex',
				flexDirection: 'column',
				justifyContent: 'flex-start',
				alignItems: 'center',
				// Use dvh (dynamic viewport height) for mobile to account for browser chrome
				// Fallback to 100vh for older browsers, then override with dvh if supported
				height: '100vh',
				'@supports (height: 100dvh)': {
					height: '100dvh',
				},
				// iOS Safari fallback
				'@supports (-webkit-touch-callout: none)': {
					minHeight: '-webkit-fill-available',
				},
				maxHeight: '100vh',
				'@supports (max-height: 100dvh)': {
					maxHeight: '100dvh',
				},
				borderRadius: '20px 20px 0 0',
				bgcolor: '#F9FAFB',
				boxShadow: '0px -4px 10px 0px rgba(0, 0, 0, 0.06)',
				overflow: 'hidden',
			}}>
				{/* Drawer Container */}
				{/* Handle */}
				<Box sx={{
					position: 'absolute',
					top: '8px',
					left: '50%',
					transform: 'translateX(-50%)',
					width: '50px',
					height: '6px',
					bgcolor: '#D9D9D9',
					borderRadius: '1000px'
				}} />

				{/* Header */}
				<Box sx={{
					display: 'flex',
					flexDirection: 'column',
					gap: '10px',
					pt: '24px',
					px: '16px',
					flexShrink: 0
				}}>
					{/* Title Row */}
					<Box sx={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
						gap: '10px'
					}}>
						<Box sx={{ width: '60px' }} /> {/* Spacer */}
						<Typography sx={{
							fontFamily: 'Roboto, sans-serif',
							fontWeight: 400,
							fontSize: '16px',
							lineHeight: 1.5,
							color: '#2D3748',
							textAlign: 'center',
							flex: 1
						}}>
							{title}
						</Typography>
						<Box sx={{ width: '60px' }} /> {/* Spacer */}
					</Box>

					{/* Progress Indicator */}
					{showProgress && <ProgressIndicator currentStep={currentStep} />}
				</Box>

				{/* Content */}
				<Box
					ref={contentRef}
					sx={{
						display: 'flex',
						flexDirection: 'column',
						gap: '18px',
						p: '18px 16px',
						pb: hideNavigation ? '44px' : '120px', // Extra padding for fixed nav
						overflowY: 'auto',
						overflowX: 'hidden',
						flex: 1,
						width: '100%',
						minHeight: 0,
					}}
				>
					{/* Subtitle */}
					{subtitle && (
						<Typography sx={{
							fontFamily: 'Inter',
							fontWeight: 600,
							fontSize: '24px',
							lineHeight: 1.21,
							color: '#2D3748'
						}}>
							{subtitle}
						</Typography>
					)}

					{/* Step Content */}
					{children}
				</Box>
				{/* Fixed Navigation Buttons */}
				{!hideNavigation && (
					<NavigationButtons
						onBack={onBack}
						onNext={onNext}
						backLabel={backLabel}
						nextLabel={nextLabel}
						showBack={showBack}
						showNext={showNext}
						nextDisabled={nextDisabled}
						loading={loading}
						isMobile={true}
					/>
				)}
			</Box>
		)
	}

	// Desktop: Centered card layout
	return (
		<Box sx={{
			minHeight: '100%',
			display: 'flex',
			flexDirection: 'column',
			justifyContent: 'center',
			alignItems: 'center',
			gap: '10px',
			padding: '24px',
			background: 'linear-gradient(180deg, #F7F6F3 37%, #F1E9FC 100%)'
		}}>
			{/* Card Container */}
			<Box sx={{
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'center',
				gap: '22px',
				padding: '16px 24px',
				width: '100%',
				maxWidth: `${maxWidth}px`,
				bgcolor: '#FFFFFF',
				borderRadius: '12px',
				boxShadow: '0px 4px 4px -4px rgba(12, 12, 13, 0.05), 0px 16px 32px -4px rgba(12, 12, 13, 0.1)'
			}}>
				{/* Header Row */}
				<Box sx={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					width: '100%',
					gap: '18px'
				}}>
					<Typography sx={{
						fontFamily: 'Roboto, sans-serif',
						fontWeight: 400,
						fontSize: '22px',
						lineHeight: 1.27,
						color: '#2D3748'
					}}>
						{title}
					</Typography>
					<Box sx={{ width: '40px' }} /> {/* Spacer for close button area */}
				</Box>

				{/* Progress Indicator */}
				{showProgress && <ProgressIndicator currentStep={currentStep} />}

				{/* Subtitle */}
				{subtitle && (
					<Typography sx={{
						fontFamily: 'Inter',
						fontWeight: 600,
						fontSize: '28px',
						lineHeight: 1.29,
						color: '#2D3748',
						width: '100%'
					}}>
						{subtitle}
					</Typography>
				)}

				{/* Step Content */}
				<Box sx={{ width: '100%' }}>
					{children}
				</Box>

				{/* Navigation Buttons */}
				{!hideNavigation && (
					<NavigationButtons
						onBack={onBack}
						onNext={onNext}
						backLabel={backLabel}
						nextLabel={nextLabel}
						showBack={showBack}
						showNext={showNext}
						nextDisabled={nextDisabled}
						loading={loading}
						isMobile={false}
					/>
				)}
			</Box>
		</Box>
	)
}

export default BookingStepWrapper

